import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

const StandardDropdown = forwardRef(function StandardDropdown({
  value,
  options = [],
  placeholder = 'Select an option...',
  onChange,
  className = '',
  disabled = false,
  variant = 'default',
  error = false,
  id,
}, ref) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => triggerRef.current?.focus(),
    open: () => setOpen(true),
  }));

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(e) {
      if (!wrapperRef.current?.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleToggle() {
    if (disabled) return;
    setOpen((prev) => !prev);
  }

  function handleKeyDown(e) {
    if (disabled) return;

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  function handleOptionClick(opt) {
    if (disabled || opt.disabled) return;
    onChange?.(opt.value);
    setOpen(false);
  }

  const variantClass =
    variant === 'filter'
      ? 'custom-dropdown--filter'
      : variant === 'panel-status'
        ? 'panel-status-select'
        : variant === 'override-type'
          ? 'override-type-select'
          : '';

  const statusPillClass =
    variant === 'panel-status' && value != null && value !== ''
      ? `badge-${String(value).replace(/-/g, '_')}`
      : '';

  const wrapperClass =
    variant === 'override-type' ? 'override-type-dropdown' : 'dropdown-wrapper';

  const menuClass =
    variant === 'override-type' ? 'dropdown-menu override-type-menu' : 'dropdown-menu';

  return (
    <div className={`${wrapperClass} ${className}`.trim()} ref={wrapperRef}>
      <div
        id={id}
        ref={triggerRef}
        className={[
          variant === 'override-type' ? '' : 'custom-dropdown',
          variantClass,
          statusPillClass,
          !selectedOption && variant === 'override-type' ? 'placeholder-active' : '',
          open ? 'open' : '',
          disabled ? 'disabled' : '',
          error ? 'field-error' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        tabIndex={disabled ? -1 : 0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled}
      >
        <span className="dropdown-text">
          {selectedOption?.label || placeholder}
        </span>

        <svg
          className="dropdown-arrow"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path fill="currentColor" d="M6 9L1 4h10z" />
        </svg>
      </div>

      <div className={`${menuClass} ${open ? 'show' : ''}`} role="listbox">
        {options.map((opt) => (
          <div
            key={String(opt.value)}
            className={[
              'dropdown-item',
              opt.value === value ? 'active' : '',
              opt.disabled ? 'disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleOptionClick(opt)}
            role="option"
            aria-selected={opt.value === value}
            aria-disabled={opt.disabled ? 'true' : 'false'}
          >
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
});

export default StandardDropdown;